import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FetchParams, FetchResponse, KeyValuePair, SortModel } from './models';
import { GridReportTable } from './components/grid-report-table/GridReportTable';
import { buildQueryParams } from './functions/buildQueryParams';
import { parseFilters } from './functions/parseFilters';
import { processFilterUpdate } from './functions/processFilterUpdate';
import { parseOrders } from './functions/parseOrders';
import { fetchData } from './functions/fetchData';
import { GridReportHeader } from './components/grid-report-header/GridReportHeader';
import { Report } from 'core/slices/models';
import { getFilterAndOrderParams } from './functions/getFilterAndOrderParams';
import type { AppDispatch } from 'core/store';
import { fetchFilterOptions, GridFilterOptions } from './functions/fetchFilterOptions';
import { ensureBuCodeFilter } from './functions/buCodeFilter';

type GridReportViewProps = {
  selectedReport: string;
  activeReport: Report;
  activeProjectKey: string;
};

export const GridReportView: React.FC<GridReportViewProps> = ({
  selectedReport,
  activeReport,
  activeProjectKey,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterValues, setFilterValues] = useState<KeyValuePair[]>([]);
  const [orderModel, setOrderModel] = useState<SortModel[]>([]);
  const [isUrlParsed, setIsUrlParsed] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<GridFilterOptions | null>(null);
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  //On initial load: read params
  useEffect(() => {
    const { filterParam, orderParam } = getFilterAndOrderParams(searchParams);
    setFilterValues(parseFilters(filterParam));
    setOrderModel(parseOrders(orderParam));
    setIsUrlParsed(true);
  }, [searchParams]);

  useEffect(() => {
    let isCurrent = true;
    fetchFilterOptions(dispatch, selectedReport, activeProjectKey)
      .then(options => {
        if (isCurrent) setFilterOptions(options);
      })
      .catch((error: unknown) => {
        if (isCurrent) {
          setFilterOptionsError(error instanceof Error ? error.message : 'Unknown error');
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [dispatch, selectedReport, activeProjectKey]);

  const effectiveFilterValues = useMemo(
    () =>
      filterOptions
        ? ensureBuCodeFilter(filterValues, filterOptions.defaultBuCode)
        : filterValues,
    [filterValues, filterOptions]
  );

  // When your table requests a new fetch, update URL in browser separately by using the filter values
  const syncUrlFromParams = (params: FetchParams) => {
    const q = buildQueryParams(params);
    if (searchParams.toString() !== q.toString()) {
      setSearchParams(q, { replace: true });
    }
  };

  const fetchDataWrapper = (params: FetchParams): Promise<FetchResponse> => {
    return fetchData(
      params,
      selectedReport,
      activeProjectKey,
      dispatch,
      isUrlParsed,
      syncUrlFromParams
    );
  };

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilterValues(prevFilters =>
      processFilterUpdate(prevFilters, field, value)
    );
  };

  if (filterOptionsError) {
    return <div role="alert">Failed to load report filters: {filterOptionsError}</div>;
  }

  if (!isUrlParsed || !filterOptions) return <div>Loading...</div>;

  const handleClearFilters = () => {
    setFilterValues([]);
    setOrderModel([]);
  };

  return (
    <>
      <GridReportHeader activeReport={activeReport} />
      <Row>
        <GridReportTable
          key={selectedReport}
          reportId={selectedReport}
          fetchData={fetchDataWrapper}
          pageSize={50}
          filterValues={effectiveFilterValues}
          orderModel={orderModel}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          countries={filterOptions.countries}
        />
      </Row>
    </>
  );
};
